// ============================================================
// AI Rådgiver — Salg og lønnsomhet: dom, grep og salgsscenarioer
// ============================================================
// Bygger på salgsmodellen i aiAdvisorSales.jsx og legger til det rådgivende laget:
// hva som er situasjonen, hva jeg ville gjort først, og hva som skjer hvis.
//
// To regler gjennomsyrer fila:
//  1. Ingenting foreslås uten et signal bak. Et grep som ikke har et tall å peke på,
//     tas ikke med — da er det en gjetning, ikke et råd.
//  2. Plumme har ingen kostpris per produkt. Alt som ville krevd bruttomargin er
//     merket som forutsetning i scenarioet, ikke skjult i et tall.
const PlummeAiAdvisorSalesAdvice = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const round1 = (value) => Math.round(num(value) * 10) / 10;

  // Terskler. Samlet her fordi de er vurderinger, ikke regnskapsregler — de skal være
  // lette å finne og justere ett sted.
  const SLOW_PAYER_DAYS = 30;
  const DISCOUNT_ATTENTION_PCT = 2;
  const CONCENTRATION_PCT = 40;
  const RECURRING_TARGET_PCT = 20;
  const PRICE_TEST_PCT = 5;
  const VOLUME_DROP_PCT = 5;

  // ---------- Dom ----------
  // Én setning om hva som faktisk preger salget nå. Rekkefølgen er prioritert: det
  // som truer inntekten kommer før det som bare er verdt å vite.
  const buildVerdict = (model) => {
    const kpis = model.kpis || {};
    const slow = safeArray(model.customers).filter((customer) => num(customer.avgPaymentDays, 0) > SLOW_PAYER_DAYS);
    if (kpis.topCustomerSharePct != null && kpis.topCustomerSharePct > CONCENTRATION_PCT) {
      return { key: 'concentration', tone: 'amber', vars: { name: kpis.topCustomerName, pct: kpis.topCustomerSharePct, revenue: model.revenue } };
    }
    if (slow.length) {
      return { key: 'slowPayers', tone: 'amber', vars: { count: slow.length, days: Math.max(...slow.map((customer) => num(customer.avgPaymentDays))) } };
    }
    if (num(kpis.avgDiscountPct) >= DISCOUNT_ATTENTION_PCT) {
      return { key: 'discounts', tone: 'amber', vars: { pct: kpis.avgDiscountPct, amount: model.discountAmount } };
    }
    if (kpis.revenueChangePct != null && kpis.revenueChangePct > 0) {
      return { key: 'growing', tone: 'green', vars: { pct: kpis.revenueChangePct, revenue: model.revenue } };
    }
    if (kpis.revenueChangePct != null && kpis.revenueChangePct < 0) {
      return { key: 'shrinking', tone: 'red', vars: { pct: Math.abs(kpis.revenueChangePct), revenue: model.revenue } };
    }
    return { key: 'steady', tone: 'green', vars: { revenue: model.revenue, count: model.invoiceCount } };
  };

  // ---------- Dette ville jeg gjort først ----------
  // Hvert grep bærer sitt eget tall. `effectKind` sier om tallet er kroner, dager eller
  // prosent, slik at UI aldri formatterer dager som beløp.
  const buildMoves = (model) => {
    const kpis = model.kpis || {};
    const moves = [];
    const slow = safeArray(model.customers)
      .filter((customer) => num(customer.avgPaymentDays, 0) > SLOW_PAYER_DAYS)
      .sort((a, b) => num(b.avgPaymentDays) - num(a.avgPaymentDays));
    if (slow.length) {
      moves.push({
        key: 'chaseSlowPayers',
        icon: 'clock',
        vars: { count: slow.length, name: slow[0].name, days: num(slow[0].avgPaymentDays) },
        // Effekten er kortere kredittid. Kronebeløpet avhenger av når kunden faktisk
        // betaler, så vi oppgir dagene — ikke et beløp vi ikke kan stå inne for.
        effectKind: 'days',
        effectValue: Math.round(num(slow[0].avgPaymentDays) - num(kpis.avgPaymentDays, 0)),
        effectType: 'time',
        confidence: 'high',
        route: 'fakturaliste',
      });
    }
    if (num(model.discountAmount) > 0 && num(kpis.avgDiscountPct) >= DISCOUNT_ATTENTION_PCT) {
      moves.push({
        key: 'reviewDiscounts',
        icon: 'percent',
        vars: { pct: kpis.avgDiscountPct, amount: model.discountAmount },
        effectKind: 'amount',
        effectValue: Math.round(num(model.discountAmount) / 2),
        effectType: 'income',
        confidence: 'medium',
        route: 'fakturaliste',
      });
    }
    if (kpis.topCustomerSharePct != null && kpis.topCustomerSharePct > CONCENTRATION_PCT) {
      moves.push({
        key: 'spreadCustomers',
        icon: 'users',
        vars: { name: kpis.topCustomerName, pct: kpis.topCustomerSharePct },
        effectKind: 'percent',
        effectValue: kpis.topCustomerSharePct,
        effectType: 'risk',
        confidence: 'high',
        route: 'kunder',
      });
    }
    if (num(model.revenue) > 0) {
      moves.push({
        key: 'testPriceIncrease',
        icon: 'trendUp',
        vars: { pct: PRICE_TEST_PCT },
        effectKind: 'amount',
        effectValue: Math.round(num(model.revenue) * PRICE_TEST_PCT / 100),
        effectType: 'income',
        // Middels: effekten forutsetter at volumet holder seg, og det vet vi ikke.
        confidence: 'medium',
        route: 'produkter',
      });
    }
    if (num(model.revenue) > 0 && num(kpis.recurringSharePct) < RECURRING_TARGET_PCT) {
      moves.push({
        key: 'growRecurring',
        icon: 'refresh',
        vars: { pct: kpis.recurringSharePct, target: RECURRING_TARGET_PCT },
        effectKind: 'amount',
        effectValue: Math.round(num(model.revenue) * (RECURRING_TARGET_PCT - num(kpis.recurringSharePct)) / 100),
        effectType: 'income',
        confidence: 'low',
        route: 'abonnement',
      });
    }
    return moves.slice(0, 3);
  };

  // ---------- Salgsscenarioer ----------
  // Hvert scenario gir før/etter på fire nøkkeltall. Resultatet endres krone for krone
  // med omsetningen: uten kostpris per produkt kan vi ikke regne ut hvor mye av en
  // omsetningsendring som spises av variable kostnader, og da er det ærligere å si det
  // i forutsetningene enn å gjette på en margin.
  const scenarioRows = ({ revenue, result, liquidity, deltaRevenue, deltaResult }) => {
    const nextRevenue = Math.round(revenue + deltaRevenue);
    const nextResult = Math.round(result + deltaResult);
    return {
      before: { revenue: Math.round(revenue), result: Math.round(result), margin: revenue > 0 ? round1((result / revenue) * 100) : null, liquidity: Math.round(liquidity) },
      after: { revenue: nextRevenue, result: nextResult, margin: nextRevenue > 0 ? round1((nextResult / nextRevenue) * 100) : null, liquidity: Math.round(liquidity + deltaResult) },
    };
  };

  const buildScenarios = (model, context = {}, inputs = {}) => {
    const kpis = model.kpis || {};
    const revenue = num(model.revenue);
    const result = num(context.result);
    const liquidity = num(context.liquidity);
    const list = [];
    if (revenue > 0) {
      const pricePct = Math.max(0, Math.min(100, num(inputs.priceUp, PRICE_TEST_PCT)));
      const volumePct = Math.max(0, Math.min(100, num(inputs.volumeDown, VOLUME_DROP_PCT)));
      const delta = Math.round(revenue * pricePct / 100);
      list.push({
        id: 'priceUp', icon: 'trendUp', slider: { min: 0, max: 100, step: 1, value: pricePct },
        // En prisøkning gir ingen ekstra vare- eller timekostnad, så hele beløpet
        // slår gjennom på resultatet.
        ...scenarioRows({ revenue, result, liquidity, deltaRevenue: delta, deltaResult: delta }),
        tone: 'green', summaryVars: { pct: pricePct, delta },
        notes: { assumptions: 'priceUp', confidence: 'medium', risk: 'priceUp', scope: 'all' },
      });
      const drop = Math.round(revenue * volumePct / 100);
      list.push({
        id: 'volumeDown', icon: 'trendDown', slider: { min: 0, max: 100, step: 1, value: volumePct },
        ...scenarioRows({ revenue, result, liquidity, deltaRevenue: -drop, deltaResult: -drop }),
        tone: 'amber', summaryVars: { pct: volumePct, delta: drop },
        notes: { assumptions: 'volumeDown', confidence: 'low', risk: 'volumeDown', scope: 'all' },
      });
    }
    if (num(model.discountAmount) > 0) {
      const delta = Math.round(num(model.discountAmount) / 2);
      list.push({
        id: 'halveDiscounts', icon: 'percent', qVars: {},
        ...scenarioRows({ revenue, result, liquidity, deltaRevenue: delta, deltaResult: delta }),
        tone: 'green', summaryVars: { delta, amount: model.discountAmount, pct: kpis.avgDiscountPct },
        notes: { assumptions: 'halveDiscounts', confidence: 'medium', risk: 'halveDiscounts', scope: 'discounted' },
      });
    }
    const top = safeArray(model.customers)[0];
    if (top && num(top.revenue) > 0) {
      const delta = num(top.revenue);
      list.push({
        id: 'loseTopCustomer', icon: 'alertTri', qVars: { name: top.name },
        ...scenarioRows({ revenue, result, liquidity, deltaRevenue: -delta, deltaResult: -delta }),
        tone: 'red', summaryVars: { name: top.name, delta, pct: kpis.topCustomerSharePct },
        notes: { assumptions: 'loseTopCustomer', confidence: 'high', risk: 'loseTopCustomer', scope: 'topCustomer' },
      });
    }
    if (num(model.recurringRevenue) > 0) {
      const delta = Math.round(num(model.recurringRevenue) * 0.1);
      list.push({
        id: 'growRecurring', icon: 'refresh', qVars: { pct: 10 },
        ...scenarioRows({ revenue, result, liquidity, deltaRevenue: delta, deltaResult: delta }),
        tone: 'green', summaryVars: { pct: 10, delta, share: kpis.recurringSharePct },
        notes: { assumptions: 'growRecurring', confidence: 'medium', risk: 'growRecurring', scope: 'recurring' },
      });
    }
    return list;
  };

  const buildSalesAdvice = (model, context = {}, scenarioInputs = {}) => {
    if (!model || !model.invoiceCount) return null;
    const moves = buildMoves(model);
    return {
      verdict: buildVerdict(model),
      moves,
      scenarios: buildScenarios(model, context, scenarioInputs),
      // Avslutningsraden er de samme grepene, ikke en ny liste — ellers ville siden
      // foreslått noe annet nederst enn den gjorde øverst.
      closer: moves.map((move) => ({ key: move.key, route: move.route })),
    };
  };

  return { buildSalesAdvice, buildVerdict, buildMoves, buildScenarios };
})();

if (typeof globalThis !== 'undefined') globalThis.PlummeAiAdvisorSalesAdvice = PlummeAiAdvisorSalesAdvice;
if (typeof module !== 'undefined' && module.exports) module.exports = PlummeAiAdvisorSalesAdvice;
